import { AppIcon, type ColorToken, DotBadge, formatCurrency } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LineInfoIcon } from '@backrs_ui/grow-ui/src/components/ResourceWrapper/LineInfoIcon';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { selectProfilePhoto } from '@backrs_ui/grow-ui/src/redux/user/user';
import {
  AWARD_AMOUNT_FIELDS,
  AwardAmountField,
  AwardAmounts,
} from '@backrs_ui/grow-ui/src/redux/userColleges/userColleges.types';

import { Avatar, Box, IconButton, SxProps, Theme, Typography, useTheme } from '@mui/material';

import { hasScholarships } from '../awardLetter.util';

const STRINGS = appStrings.modules.myColleges.awardLetter;

const AMOUNT_COLORS: Record<AwardAmountField, ColorToken> = {
  cost_of_attendance: 'Error/600',
  grants: 'Success/600',
  scholarships: 'Success/600',
  loans: 'Error/600',
  work_study: 'Warning/600',
};

type NetCostCardProps = Readonly<{
  amounts: AwardAmounts;
  netCost: number | null;
  percentFromEstimate: number | null;
  fromLetter: boolean;
  onEdit: (field: AwardAmountField) => void;
}>;

export const NetCostCard = ({
  amounts,
  netCost,
  percentFromEstimate,
  fromLetter,
  onEdit,
}: NetCostCardProps) => {
  const { backrs } = useTheme();
  const profilePhoto = useAppSelector(selectProfilePhoto);

  const badgeFor = (field: AwardAmountField) => {
    if (field === 'loans') {
      return <DotBadge label={STRINGS.badges.youRepay} dotColor={backrs.colors['Error/500']} />;
    }
    if (field === 'scholarships' && hasScholarships(amounts)) {
      return <DotBadge label={STRINGS.badges.followUp} dotColor={backrs.colors['Brand/500']} />;
    }

    return null;
  };

  return (
    <Box id="award-letter-net-cost" sx={cardSx}>
      <Box sx={headerSx}>
        <Box sx={titleSx}>
          <Avatar src={profilePhoto ?? undefined} alt="" sx={avatarSx} />
          <Typography variant="text-sm-semibold">{STRINGS.netCost.title}</Typography>
        </Box>
        <LineInfoIcon
          idPrefix="award-letter-net-cost"
          iconColor="Gray/400"
          info={{ kind: 'tooltip', text: STRINGS.netCost.tooltip }}
        />
      </Box>

      <Box sx={summarySx}>
        {netCost === null ? (
          <Typography variant="text-md-regular" sx={mutedSx}>
            {STRINGS.netCost.missing}
          </Typography>
        ) : (
          <>
            <Box sx={amountSx}>
              <Typography variant="display-sm-semibold">{formatCurrency(netCost)}</Typography>
              <Typography variant="text-sm-medium" sx={mutedSx}>
                {STRINGS.netCost.perYear}
              </Typography>
            </Box>
            {percentFromEstimate !== null && (
              <Box id="award-letter-vs-estimate" sx={trendSx}>
                {percentFromEstimate !== 0 && (
                  <AppIcon
                    iconName={percentFromEstimate > 0 ? 'ArrowUp' : 'ArrowDown'}
                    diameter="16px"
                    color={percentFromEstimate > 0 ? 'Error/600' : 'Success/600'}
                  />
                )}
                <Typography variant="text-sm-regular">
                  {STRINGS.netCost.vsEstimate(percentFromEstimate)}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      {fromLetter && (
        <Typography variant="text-xs-semibold" sx={fromLetterSx}>
          {STRINGS.netCost.fromLetter}
        </Typography>
      )}

      <Box component="dl" sx={rowsSx}>
        {AWARD_AMOUNT_FIELDS.map((field) => (
          <Box key={field} id={`award-letter-row-${field.replace(/_/g, '-')}`} sx={rowSx}>
            <Box component="dt" sx={rowLabelSx}>
              <Typography variant="text-sm-medium" sx={rowLabelTextSx}>
                {STRINGS.fields[field].label}
              </Typography>
              {badgeFor(field)}
            </Box>
            <Typography
              component="dd"
              variant="text-sm-regular"
              sx={amountValueSx(AMOUNT_COLORS[field])}
            >
              {amounts[field] === null ? '—' : formatCurrency(amounts[field])}
            </Typography>
            <IconButton
              id={`award-letter-edit-${field.replace(/_/g, '-')}`}
              aria-label={STRINGS.edit.title(STRINGS.fields[field].label)}
              onClick={() => onEdit(field)}
              sx={editButtonSx}
            >
              <AppIcon iconName="Edit01" diameter="16px" color="Gray/400" />
            </IconButton>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const cardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  overflow: 'hidden',
});

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingInline: backrs.spacing['spacing-xl'],
  paddingBlock: backrs.spacing['spacing-md'],
  borderBottom: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const avatarSx: SxProps<Theme> = ({ backrs }) => ({
  width: backrs.spacing['spacing-3xl'],
  height: backrs.spacing['spacing-3xl'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
});

const summarySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-md'],
  padding: backrs.spacing['spacing-xl'],
});

const amountSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: backrs.spacing['spacing-md'],
});

const trendSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
});

const fromLetterSx: SxProps<Theme> = ({ backrs }) => ({
  paddingInline: backrs.spacing['spacing-xl'],
  paddingBottom: backrs.spacing['spacing-md'],
  color: backrs.colors['Colors/Text/text-quaternary'],
});

const rowsSx: SxProps<Theme> = {
  margin: 0,
};

const rowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: { xs: 'minmax(0, 1fr) auto auto', sm: 'minmax(0, 1fr) 122px 94px' },
  alignItems: 'center',
  minHeight: '59px',
  borderTop: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
});

const cellSx = ({ backrs }: Theme) => ({
  paddingInline: backrs.spacing['spacing-3xl'],
  paddingBlock: backrs.spacing['spacing-xl'],
});

const rowLabelSx: SxProps<Theme> = (theme) => ({
  ...cellSx(theme),
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.backrs.spacing['spacing-sm'],
  margin: 0,
});

const rowLabelTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const amountValueSx =
  (color: ColorToken): SxProps<Theme> =>
  (theme) => ({
    ...cellSx(theme),
    margin: 0,
    color: theme.backrs.colors[color],
  });

const editButtonSx: SxProps<Theme> = ({ backrs }) => ({
  justifySelf: 'start',
  marginInline: backrs.spacing['spacing-xl'],
  padding: backrs.spacing['spacing-md'],
  borderRadius: backrs.radius['radius-sm'],
});

const mutedSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
